import { Box, Skeleton } from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { useContext, useEffect, useState } from "react";

import { useGamification } from "utils/useGamification";

import CardHeader from "./components/CardHeader";
import CreateNicheTemplateCard from "./components/CreateNicheTemplateCard";
import CreateNicheTemplateCardLoading from "./components/CreateNicheTemplateCardLoading";
import WeeklyConsistencyChallenge from "./components/WeeklyConsistencyChallenge";
import WeeklyConsistencyChallengeLoading from "./components/WeeklyConssitencyChallengeLoading";
import {
  countCreditByTask,
  countTaskByType,
  displayFirstTemplateCard,
  displayFirstTemplateCardOnlyForVa,
} from "./helpers";
import { OrganizationGamificationTask } from "./schema";
import styles from "./styles";

const Gamification = () => {
  const { currentOrg, gamificationProfile, credits } = useContext(
    OrganizationUserContext,
  );
  const { outreachTemplates, fetchOutreachTemplates } = useContext(
    OutreachTemplatesContext,
  );
  const {
    fetchGamificationProfile,
    profileLoading,
    refreshGamificationProfile,
    fetchCredits,
  } = useGamification();
  const [numOfCardsToRender, setNumOfCardsToRender] = useState<number | null>(
    null,
  );

  const isTaskCompleted = (
    taskType: OrganizationGamificationTask,
    index: number,
  ) => {
    if (!gamificationProfile) return false;
    return countTaskByType(gamificationProfile, taskType) >= index;
  };

  const computeNicheCardToRender = () => {
    if (!gamificationProfile) return 0;

    if (
      currentOrg?.isProUser &&
      displayFirstTemplateCard(outreachTemplates, gamificationProfile)
    ) {
      return 1;
    }

    if (currentOrg?.isVaUser) {
      const displayOneCardOnly = displayFirstTemplateCardOnlyForVa(
        gamificationProfile,
        credits,
      );
      if (displayOneCardOnly) {
        return 1;
      }
      return 2;
    }
    return 0;
  };

  const handleFetchProfile = () => {
    if (!gamificationProfile) {
      fetchGamificationProfile();
    } else if (
      gamificationProfile?.id &&
      currentOrg?.isProUser &&
      !gamificationProfile?.emailStreaks
    ) {
      refreshGamificationProfile();
    }
  };

  useEffect(() => {
    if (currentOrg?.id) {
      fetchCredits();
      handleFetchProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id, gamificationProfile?.id]);

  useEffect(() => {
    if (
      gamificationProfile &&
      !numOfCardsToRender &&
      outreachTemplates?.length > 0 &&
      !profileLoading
    ) {
      const numOfCards = computeNicheCardToRender();
      setNumOfCardsToRender(numOfCards);
    } else if (
      currentOrg?.id &&
      (!outreachTemplates || outreachTemplates?.length === 0)
    ) {
      fetchOutreachTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentOrg?.id,
    profileLoading,
    outreachTemplates?.length,
    gamificationProfile?.id,
  ]);

  if (profileLoading) {
    return (
      <Box sx={styles.paper}>
        {/* Card header skeleton */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Skeleton
            variant="rounded"
            width={48}
            height={48}
            sx={{ borderRadius: "10px" }}
          />
          <Box>
            <Skeleton variant="text" width={180} height={28} />
            <Skeleton variant="text" width={140} height={20} />
          </Box>
        </Box>
        <CreateNicheTemplateCardLoading />
        <WeeklyConsistencyChallengeLoading />
      </Box>
    );
  }

  const renderTemplateCard = (title = "Create a Niche-Specific Template") => {
    return (
      <CreateNicheTemplateCard
        title={title}
        description="Customize your pitch for a specific niche (e.g., beauty, fitness, tech)"
        completed={isTaskCompleted(
          OrganizationGamificationTask.niche_templates,
          1,
        )}
        infoBox={
          <>
            {" "}
            <strong>Pro tip:</strong> Different niches respond to different
            pitching styles. A beauty brand cares about aesthetics and
            engagement, while a tech brand wants to see expertise and audience
            demographics.
          </>
        }
      />
    );
  };

  return (
    <Box sx={styles.paper}>
      {currentOrg?.isProUser && (
        <>
          <CardHeader
            title="Pitching Challenges"
            description="Unlock Bento Site Credit!"
            icon="fa-solid fa-bullseye"
          />
          {numOfCardsToRender && numOfCardsToRender > 0
            ? renderTemplateCard()
            : null}
          <WeeklyConsistencyChallenge
            completed={isTaskCompleted(
              OrganizationGamificationTask.email_streak,
              1,
            )}
          />
        </>
      )}
      {currentOrg?.isVaUser && numOfCardsToRender && numOfCardsToRender > 0 ? (
        <>
          <CardHeader
            title="Niche Templates Challenges"
            description="Earn credits for creating more templates"
            icon="fa-solid fa-pen"
          />
          {Array.from({ length: numOfCardsToRender }).map((_, index) => (
            <CreateNicheTemplateCard
              key={index}
              title={
                numOfCardsToRender <= 1
                  ? `Niche Template`
                  : `Niche Template #${index + 1}`
              }
              description={
                index === 0
                  ? "Create a pitch template for a specific niche (e.g., beauty, fitness, tech)"
                  : "Create a pitch template for a second niche to diversify your opportunities"
              }
              completed={
                countCreditByTask(
                  credits,
                  OrganizationGamificationTask.niche_templates,
                ) >=
                index + 1
              }
              infoBox={
                <>
                  {" "}
                  {index === 0 && (
                    <>
                      <strong>Pro tip:</strong> Different niches respond to
                      different pitching styles. A beauty brand cares about
                      aesthetics and engagement, while a tech brand wants to see
                      expertise and audience demographics.
                    </>
                  )}
                  {index === 1 && (
                    <>
                      <strong>Why a second niche?</strong> Having templates for
                      multiple niches increases your opportunities and helps you
                      understand which brand categories respond best to your
                      content style.
                    </>
                  )}
                </>
              }
            />
          ))}
        </>
      ) : (
        <></>
      )}
    </Box>
  );
};

export default Gamification;
